"use client";

/**
 * Single-select with a filter box — the old front used `searchable: true` for every
 * reference list on the registration forms (районы, населённые пункты, школы, регионы),
 * and those lists run into the hundreds of options.
 *
 * Labels wrap instead of truncating: one district can hold several schools whose names
 * differ only past the cut — on the astana-daryny form every BINOM broke off at the same
 * `ТОО "BINOM EDUCATION" Школа-лицей BINOM SCHOOL и...`, leaving nothing to choose by.
 */

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type SearchableOption<TValue extends string | number> = {
  value: TValue;
  label: string;
};

function cx(...classes: Array<string | null | undefined | false>) {
  return classes.filter(Boolean).join(" ");
}

export function SearchableSelect<TValue extends string | number>({
  options,
  value,
  onChange,
  placeholder,
  invalid,
  disabled,
}: {
  options: SearchableOption<TValue>[];
  value: TValue | null;
  onChange: (value: TValue) => void;
  placeholder: string;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const t = useTranslations();
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cx(
            "h-auto min-h-9 w-full items-start justify-between rounded-lg bg-white px-4 py-2 text-left font-normal shadow-sm",
            !selected && "text-muted-foreground",
            invalid && "border-red-500 ring-1 ring-red-500",
          )}
        >
          <span className="whitespace-normal break-words leading-snug">
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="mt-0.5 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("placeholder-filter")} />
          <CommandList>
            <CommandEmpty>{t("label-nothing-found")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={String(option.value)}
                  value={option.label}
                  className="items-start"
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cx(
                      "mt-0.5 mr-2 h-4 w-4",
                      option.value === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="whitespace-normal break-words leading-snug">
                    {option.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
